import { Helmet } from 'react-helmet-async';
import { siteConfig, getFullUrl, type SupportedLanguage } from '@/config/site';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { STATIC_TRANSLATIONS, PRODUCT_SILOS } from '@/config/urlTranslations';
import { useProductSlugTranslationsData, getLocalizedProductUrl, type ProductSlugTranslation } from '@/hooks/useProductSlugTranslations';

// Single source of truth: public/seo-metadata.json
// Edge function fetches this at runtime, Vite bundles it at build time
import seoMetadata from '../../public/seo-metadata.json';

// Default SEO data loaded from central JSON file
const defaultSEO: Record<string, { title: string; description: string; keywords?: string }> = seoMetadata;

export interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  language?: SupportedLanguage;
  /** Override auto-generated hreflang URLs (e.g. for blog articles with per-language slugs) */
  customHreflangUrls?: Array<{ lang: string; url: string }>;
  /** Product slug translations for localized canonical/hreflang URLs */
  productSlugTranslations?: ProductSlugTranslation[];
  // For product pages
  product?: {
    name: string;
    price: number;
    currency?: string;
    image?: string;
    availability?: 'InStock' | 'OutOfStock';
  };
}

/**
 * Resolve the localized slug for a given EN canonical path and target language.
 */
function getLocalizedSlug(
  canonicalPath: string,
  lang: SupportedLanguage,
  productSlugTranslations?: ProductSlugTranslation[]
): string {
  const cleanPath = canonicalPath === '/' ? '/' : canonicalPath.replace(/\/$/, '');

  for (const t of STATIC_TRANSLATIONS) {
    if (t.en_slug === cleanPath) {
      const slugKey = `${lang}_slug` as keyof typeof t;
      const localizedSlug = t[slugKey] as string | undefined;
      if (localizedSlug) return localizedSlug;
      return cleanPath;
    }
  }

  const siloMatch = cleanPath.match(/^\/shop\/[^/]+\/(.+)$/);
  if (siloMatch) {
    const productSlug = siloMatch[1];
    if (productSlugTranslations && productSlugTranslations.length > 0) {
      const localized = getLocalizedProductUrl(productSlugTranslations, productSlug, lang);
      // getLocalizedProductUrl returns with language prefix for non-en, strip it
      const withoutLangPrefix = localized.replace(/^\/(de|es|fr|pt|ru|it|sv|zh)/, '');
      return withoutLangPrefix;
    }
    const silo = PRODUCT_SILOS[lang] || PRODUCT_SILOS.en;
    return `/${silo}/${productSlug}`;
  }

  return cleanPath;
}

/**
 * Build self-referencing canonical URL for the current language.
 */
function buildCanonicalUrl(
  canonicalPath: string,
  language: SupportedLanguage,
  productSlugTranslations?: ProductSlugTranslation[]
): string {
  const localizedSlug = getLocalizedSlug(canonicalPath, language, productSlugTranslations);

  if (language === siteConfig.defaultLanguage) {
    return getFullUrl(localizedSlug);
  }

  return getFullUrl(`/${language}${localizedSlug}`);
}

/**
 * Build hreflang URLs with correctly localized slugs for each language.
 */
function buildHreflangUrls(
  canonicalPath: string,
  productSlugTranslations?: ProductSlugTranslation[]
): Array<{ lang: string; url: string }> {
  return siteConfig.supportedLanguages.map(lang => {
    const localizedSlug = getLocalizedSlug(canonicalPath, lang, productSlugTranslations);

    if (lang === siteConfig.defaultLanguage) {
      return { lang, url: getFullUrl(localizedSlug) };
    }

    return { lang, url: getFullUrl(`/${lang}${localizedSlug}`) };
  });
}

/**
 * Look up JSON metadata for a given canonical path + language.
 */
function getJsonMeta(
  canonicalPath: string,
  language: SupportedLanguage,
  productSlugTranslations?: ProductSlugTranslation[]
) {
  const localizedLookupPath = language === 'en'
    ? (canonicalPath || '/')
    : `/${language}${getLocalizedSlug(canonicalPath || '/', language, productSlugTranslations)}`;
  return defaultSEO[localizedLookupPath] || defaultSEO[canonicalPath || '/'] || null;
}

export function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '',
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  noIndex = false,
  language = 'en',
  customHreflangUrls,
  productSlugTranslations,
  product,
}: SEOHeadProps) {
  // 1) JSON-first lookup
  const jsonMeta = getJsonMeta(canonicalPath, language, productSlugTranslations);

  // 2) Resolve final values: props > JSON > siteConfig defaults
  const resolvedTitle = title || jsonMeta?.title;
  const fullTitle = resolvedTitle
    ? (resolvedTitle.includes(siteConfig.name) ? resolvedTitle : `${resolvedTitle} | ${siteConfig.name}`)
    : `${siteConfig.name} | ${siteConfig.tagline}`;

  const fullDescription = description || jsonMeta?.description || siteConfig.description;
  const resolvedKeywords = keywords || jsonMeta?.keywords;

  // 3) Canonical & hreflang
  const canonicalUrl = buildCanonicalUrl(canonicalPath, language, productSlugTranslations);
  const hreflangUrls = customHreflangUrls || buildHreflangUrls(canonicalPath, productSlugTranslations);
  const xDefaultUrl = customHreflangUrls
    ? customHreflangUrls.find(h => h.lang === siteConfig.defaultLanguage)?.url || getFullUrl(getLocalizedSlug(canonicalPath, siteConfig.defaultLanguage, productSlugTranslations))
    : getFullUrl(getLocalizedSlug(canonicalPath, siteConfig.defaultLanguage, productSlugTranslations));

  // 4) OG image: props (product/blog image) > default og-image.jpg
  const imageUrl = ogImage || getFullUrl(siteConfig.ogImage);

  // 5) OG/Twitter tags always sync with resolved title & description
  const finalOgTitle = ogTitle || fullTitle;
  const finalOgDescription = ogDescription || fullDescription;

  return (
    <Helmet>
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {resolvedKeywords && <meta name="keywords" content={resolvedKeywords} />}
      <meta name="author" content={siteConfig.name} />
      <link rel="canonical" href={canonicalUrl} />

      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : `${language}_${language.toUpperCase()}`} />

      {/* Twitter – synced with OG */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* hreflang */}
      {hreflangUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

      {/* Product meta */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price.toString()} />
          <meta property="product:price:currency" content={product.currency || siteConfig.defaultCurrency} />
          <meta property="product:availability" content={product.availability || 'InStock'} />
        </>
      )}
    </Helmet>
  );
}


interface ProductSEOProps {
  name: string;
  slug: string;
  description?: string;
  price: number;
  image?: string;
  category?: string;
}

// Product-specific OG images (optimized 1200x630 WebP)
const PRODUCT_OG_IMAGES: Record<string, string> = {
  'state-id': '/images/og-state-id.webp',
  'photo-id': '/images/og-photo-id.webp',
  'drivers-license': '/images/og-drivers-license.webp',
  'national-id': '/images/og-national-id.webp',
  'student-id': '/images/og-student-id.webp',
  'press-card': '/images/og-press-card.webp',
  'fishing-license': '/images/og-fishing-license.webp',
  'boater-license': '/images/og-boater-license.webp',
};

export function ProductSEO({ name, slug, description, price, image, category }: ProductSEOProps) {
  const { language } = useLanguage();
  const { data: productSlugTranslations = [] } = useProductSlugTranslationsData();

  // DB fallback – only used if JSON has no entry for this product page
  const { data: seoMeta } = useSeoMeta(`product:${slug}`);

  // JSON lookup for product page
  const canonicalPath = `/shop/id-card-generator/${slug}`;
  const jsonMeta = getJsonMeta(canonicalPath, language, productSlugTranslations);

  // Priority: JSON > DB > generated defaults
  const categoryLabel = category || 'Novelty ID';
  const title = jsonMeta?.title || seoMeta?.title || `${name} | Buy ${categoryLabel} with Holograms`;
  const desc = jsonMeta?.description || seoMeta?.description || description || `Create your custom ${name} with authentic holograms and scannable barcode. Premium fake ${categoryLabel.toLowerCase()} starting at €${price}. Fast discrete shipping worldwide.`;
  const resolvedKeywords = jsonMeta?.keywords || seoMeta?.keywords || `${name.toLowerCase()}, fake ${categoryLabel.toLowerCase()}, buy ${name.toLowerCase()}, novelty ${categoryLabel.toLowerCase()}`;

  // Use product-specific OG image if available, otherwise fall back to template image
  const ogImageUrl = PRODUCT_OG_IMAGES[slug]
    ? getFullUrl(PRODUCT_OG_IMAGES[slug])
    : image;

  return (
    <SEOHead
      title={title}
      description={desc}
      keywords={resolvedKeywords}
      canonicalPath={canonicalPath}
      ogType="product"
      ogImage={ogImageUrl}
      language={language}
      productSlugTranslations={productSlugTranslations}
      product={{
        name,
        price,
        image,
        availability: 'InStock',
      }}
    />
  );
}
